import { useVehicleByVin } from '@hooks';
import { Order } from '@services';
import { useStore } from '@stores';
import { Button, Form, InputNumber, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { useView } from '@hooks';

interface IProps {
  order: Order;
  readOnly?: boolean;
}

export const MileageForm: React.FC<IProps> = observer(({ order, readOnly = false }) => {
  const { isMobile } = useView();
  const { orderStore, vehicleStore } = useStore();
  const [form] = Form.useForm();

  const vehicle = useVehicleByVin(order.vin);

  useEffect(() => {
    form.setFieldsValue({
      id: order.id,
      mileage: order.mileage,
    });
    order.mileage && form.validateFields(['mileage']);
  }, [form, order.id, order.mileage]);

  const handleUpdate = useCallback(
    async (values: any) => {
      // The API finds the vehicle by VIN (or plate when the order has none).
      await vehicleStore
        .checkVehicleMileage({
          vin: order.vin,
          registrationNumber: order.registrationNumber,
          mileage: values.mileage,
        })
        .then(async (result) => {
          if (!result) return;
          await orderStore.update({ ...values });
        });
    },
    [orderStore, vehicleStore, order.vin, order.registrationNumber]
  );

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        gap: '8px',
        justifyContent: isMobile ? 'space-between' : '',
      }}
    >
      {!readOnly ? (
        <Form
          form={form}
          style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px' }}
          onFinish={handleUpdate}
        >
          <Form.Item name="id" hidden>
            <input type="hidden" />
          </Form.Item>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Form.Item
              shouldUpdate={(prev, curr) => prev.mileage !== curr.mileage}
              style={{ marginBottom: 0 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 8,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Typography.Text style={{ fontSize: '14px', minWidth: '60px', fontWeight: 600 }}>
                  Mileage:
                </Typography.Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Form.Item
                    name="mileage"
                    noStyle
                    rules={[
                      { required: true, message: 'Mileage is required' },
                      {
                        type: 'number',
                        max: 1000000,
                        warningOnly: true,
                        message: `Maximum allowed mileage is ${'1000000'.toLocaleString()}`,
                        transform: (value) => Number(value),
                      },
                      {
                        type: 'number',
                        min: vehicle?.mileage || 0,
                        warningOnly: true,
                        message: `Entered mileage is lower than the last recorded value.`,
                        transform: (value) => Number(value),
                      },
                    ]}
                  >
                    <InputNumber
                      type="number"
                      style={{ width: 120 }}
                      controls={false}
                      changeOnWheel={false}
                      formatter={(value) => (value ? `${value}`.replace(/\./g, '') : '')}
                      parser={(value) => (value ? value.replace(/\./g, '') : '')}
                      placeholder="Enter mileage"
                    />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      loading={vehicleStore.loading || orderStore.loading}
                      disabled={vehicleStore.loading || orderStore.loading}
                      type="primary"
                      htmlType="submit"
                      size="middle"
                    >
                      Save
                    </Button>
                  </Form.Item>
                </div>
              </div>
            </Form.Item>
          </div>
        </Form>
      ) : (
        <Typography.Text>
          {order.mileage ? `${order.mileage.toLocaleString()} mls` : ''}
        </Typography.Text>
      )}
    </div>
  );
});
